import { type FC, useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, Button, Badge, Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui';
import { supabase } from '@/lib/api/supabase';
import type { Addon, RegistrationWizardState, AddonWaitlistEntry } from '@/types';
import { dbToAddon } from '@/types';
import { ShoppingBag, Minus, Plus, ChevronLeft, PackageCheck, Info, ListOrdered } from 'lucide-react';
import { useAvailabilityMonitor } from '@/hooks/data/useAvailabilityMonitor';
import { useAddonWaitlist } from '@/hooks/data/useAddonWaitlist';
import { RemoveItemConfirmDialog } from './CancelConfirmDialog';

interface AddonsSelectionStepProps {
  selections: RegistrationWizardState['addons']['selections'];
  existingPurchases?: Array<{ addonId: string; quantity: number; variantData?: Record<string, unknown> }>;
  onComplete: (data: RegistrationWizardState['addons']) => void;
  onBack: () => void;
}

/**
 * Third step of registration wizard - Add-ons selection
 * Allows users to select merchandise, apparel, and other extras
 */
export const AddonsSelectionStep: FC<AddonsSelectionStepProps> = ({
  selections: initialSelections,
  existingPurchases = [],
  onComplete,
  onBack,
}) => {
  const [addons, setAddons] = useState<Addon[]>([]);
  const [selections, setSelections] = useState(initialSelections);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [detailsAddon, setDetailsAddon] = useState<Addon | null>(null);
  const [removeConfirmState, setRemoveConfirmState] = useState<{
    isOpen: boolean;
    addonId: string;
    addonName: string;
  }>({
    isOpen: false,
    addonId: '',
    addonName: '',
  });
  
  // Addon waitlist state
  const [addonWaitlistEntries, setAddonWaitlistEntries] = useState<Record<string, AddonWaitlistEntry>>({});
  const {
    joinWaitlist: joinAddonWaitlist,
    leaveWaitlist: leaveAddonWaitlist,
    getUserWaitlistEntries,
    loading: addonWaitlistLoading,
  } = useAddonWaitlist();
  const [waitlistError, setWaitlistError] = useState<string | null>(null);

  // Use availability monitor for live stock updates
  const { currentStock, currentStockRef, subscribe, unsubscribe } = useAvailabilityMonitor();

  useEffect(() => {
    const fetchAddons = async (): Promise<void> => {
      try {
        setLoading(true);
        setError(null);

        const { data, error: fetchError } = await supabase
          .from('addons')
          .select('*')
          .eq('is_active', true)
          .order('sort_order', { ascending: true })
          .order('name', { ascending: true });

        if (fetchError) throw fetchError;

        if (data) {
          setAddons(data.map(dbToAddon));
        }
      } catch (err) {
        console.error('Failed to fetch add-ons:', err);
        setError(err instanceof Error ? err.message : 'Failed to load add-ons');
      } finally {
        setLoading(false);
      }
    };

    void fetchAddons();
  }, []);

  // Subscribe to live stock updates for all addons
  useEffect(() => {
    addons.forEach((addon) => {
      subscribe(addon.id, 'addon');
    });

    return () => {
      addons.forEach((addon) => {
        unsubscribe(addon.id, 'addon');
      });
    };
  }, [addons, subscribe, unsubscribe]);

  // Clamp selected quantities when live stock decreases.
  // Uses currentStockRef (stable identity) read on an interval to avoid
  // re-firing the effect on every poll-driven cache update.
  useEffect(() => {
    if (addons.length === 0) return;

    const clampSelections = () => {
      setSelections((prev) => {
        let hasChanges = false;
        const updated = prev.reduce<typeof prev>((acc, selection) => {
          const addon = addons.find((a) => a.id === selection.addonId);
          if (!addon || !addon.hasInventory) return [...acc, selection];

          const stock = currentStockRef(addon.id);
          if (!stock || stock.effectiveAvailable === null) return [...acc, selection];

          let effectiveMax = stock.effectiveAvailable;
          if (addon.maxPerOrder !== null) {
            effectiveMax = Math.min(effectiveMax, addon.maxPerOrder);
          }

          if (selection.quantity > effectiveMax) {
            hasChanges = true;
            if (effectiveMax <= 0) return acc;
            return [...acc, { ...selection, quantity: effectiveMax }];
          }
          return [...acc, selection];
        }, []);

        return hasChanges ? updated : prev;
      });
    };

    // Run once immediately, then on an interval aligned with polling
    clampSelections();
    const interval = setInterval(clampSelections, 10000);
    return () => clearInterval(interval);
  }, [addons, currentStockRef]);

  // Fetch user's addon waitlist entries
  useEffect(() => {
    const fetchWaitlistEntries = async () => {
      const entries = await getUserWaitlistEntries();
      const entriesMap: Record<string, AddonWaitlistEntry> = {};
      entries.forEach(e => { entriesMap[e.addonId] = e; });
      setAddonWaitlistEntries(entriesMap);
    };
    void fetchWaitlistEntries();
  }, [getUserWaitlistEntries]);

  const handleJoinAddonWaitlist = async (addonId: string, variantName?: string) => {
    const result = await joinAddonWaitlist(addonId, variantName);
    if (!result.success) {
      setWaitlistError(result.error ?? 'Failed to join waitlist');
      return;
    }
    setWaitlistError(null);
    const entries = await getUserWaitlistEntries();
    const entriesMap: Record<string, AddonWaitlistEntry> = {};
    entries.forEach(e => { entriesMap[e.addonId] = e; });
    setAddonWaitlistEntries(entriesMap);
  };

  const handleLeaveAddonWaitlist = async (addonId: string, variantName?: string) => {
    const result = await leaveAddonWaitlist(addonId, variantName);
    if (!result.success) {
      setWaitlistError(result.error ?? 'Failed to leave waitlist');
      return;
    }
    setWaitlistError(null);
    setAddonWaitlistEntries(prev => {
      const next = { ...prev };
      delete next[addonId];
      return next;
    });
  };

  // Helper to get live stock or fall back to static data
  // Now returns effective available stock (total minus reserved)
  const getLiveStock = (addon: Addon): number | null => {
    const liveStock = currentStock(addon.id);
    if (liveStock !== null) {
      // Use effective available (accounts for reserved inventory)
      return liveStock.effectiveAvailable;
    }
    // Fall back to static data
    return addon.stockQuantity;
  };

  const getQuantity = (addonId: string): number => {
    return selections.find((s) => s.addonId === addonId)?.quantity || 0;
  };

  const handleQuantityChange = (addonId: string, change: number, addon: Addon): void => {
    const currentQty = getQuantity(addonId);
    const newQty = Math.max(0, currentQty + change);

    // Check max per order
    if (addon.maxPerOrder !== null && newQty > addon.maxPerOrder) {
      return;
    }

    // Check stock using live data
    const liveStock = getLiveStock(addon);
    if (addon.hasInventory && liveStock !== null && newQty > liveStock) {
      return;
    }

    // If reducing to 0 (removing), show confirmation dialog
    if (currentQty > 0 && newQty === 0) {
      setRemoveConfirmState({
        isOpen: true,
        addonId,
        addonName: addon.name,
      });
      return;
    }

    setSelections((prev) => {
      const existing = prev.find((s) => s.addonId === addonId);

      if (newQty === 0) {
        // Remove if quantity is 0
        return prev.filter((s) => s.addonId !== addonId);
      }

      if (existing) {
        // Update existing
        return prev.map((s) =>
          s.addonId === addonId ? { ...s, quantity: newQty } : s
        );
      }

      // Add new
      return [...prev, { addonId, quantity: newQty }];
    });
  };

  const handleRemoveConfirm = (): void => {
    // Remove the addon from selections
    setSelections((prev) => prev.filter((s) => s.addonId !== removeConfirmState.addonId));
    setRemoveConfirmState({ isOpen: false, addonId: '', addonName: '' });
  };

  const handleContinue = (): void => {
    onComplete({ selections });
  };

  const calculateTotal = (): number => {
    return selections.reduce((sum, selection) => {
      const addon = addons.find((a) => a.id === selection.addonId);
      return sum + (addon?.price || 0) * selection.quantity;
    }, 0);
  };

  const totalCost = calculateTotal();
  const totalItems = selections.reduce((sum, s) => sum + s.quantity, 0);

  if (loading) {
    return (
      <Card className="backdrop-blur-md bg-turquoise-800/90 border-2 border-orange-500">
        <CardContent className="py-12 text-center">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-orange-500 border-r-transparent"></div>
          <p className="mt-4 text-orange-200">Loading merchandise...</p>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="backdrop-blur-md bg-turquoise-800/90 border-2 border-orange-500">
        <CardContent className="py-12">
          <div className="text-center">
            <ShoppingBag className="h-12 w-12 text-orange-300 mx-auto mb-4" />
            <div className="rounded-lg bg-amber-500/20 border border-amber-500/40 p-6 text-amber-200 max-w-md mx-auto">
              <p className="font-semibold">Unable to load merchandise</p>
              <p className="mt-2 text-sm">{error}</p>
            </div>
            <p className="text-sm text-orange-200 mt-4">
              No merchandise available at this time. You can continue with your registration.
            </p>
            <div className="flex gap-3 justify-center mt-6">
              <Button onClick={onBack} variant="outline" className="min-w-[120px] border-orange-500 text-orange-200 hover:bg-orange-500/20">
                <ChevronLeft className="h-4 w-4 mr-1" />
                Back
              </Button>
              <Button onClick={handleContinue} variant="secondary" className="min-w-[120px]">
                Continue to Checkout
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <RemoveItemConfirmDialog
        open={removeConfirmState.isOpen}
        onOpenChange={(open) => !open && setRemoveConfirmState({ isOpen: false, addonId: '', addonName: '' })}
        onConfirm={handleRemoveConfirm}
        itemName={removeConfirmState.addonName}
        itemType="addon"
      />

      {/* Addon Details Dialog */}
      <Dialog open={!!detailsAddon} onOpenChange={(open) => !open && setDetailsAddon(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto bg-turquoise-800 border-2 border-orange-500 text-white">
          {detailsAddon && (() => {
            const quantity = getQuantity(detailsAddon.id);
            const liveStock = getLiveStock(detailsAddon);
            const isOutOfStock = detailsAddon.hasInventory && liveStock !== null && liveStock === 0;
            const isLowStock = detailsAddon.hasInventory && liveStock !== null && liveStock <= 5 && liveStock > 0;

            return (
              <>
                <DialogHeader>
                  <DialogTitle className="text-xl font-viking text-white pr-6">
                    {detailsAddon.name}
                  </DialogTitle>
                  <div className="flex flex-wrap items-center gap-2 text-sm">
                    <Badge className="text-xs capitalize bg-orange-500 text-white">
                      {detailsAddon.category}
                    </Badge>
                    {detailsAddon.featured && (
                      <Badge className="text-xs bg-orange-600 text-white">Featured</Badge>
                    )}
                    {isLowStock && (
                      <Badge variant="destructive" className="text-xs">
                        Only {liveStock} left!
                      </Badge>
                    )}
                    {isOutOfStock && (
                      <Badge variant="destructive" className="text-xs">
                        Out of Stock
                      </Badge>
                    )}
                  </div>
                </DialogHeader>

                <div className="space-y-4">
                  {/* Image */}
                  {detailsAddon.imageUrl && (
                    <div className="rounded-lg overflow-hidden">
                      <img
                        src={detailsAddon.imageUrl}
                        alt={detailsAddon.name}
                        className="w-full h-48 object-cover"
                      />
                    </div>
                  )}

                  {/* Price section */}
                  <div className="flex items-center justify-between p-3 bg-turquoise-700/50 rounded-lg">
                    <div>
                      <p className="text-sm text-orange-200">Price</p>
                      <p className="text-2xl font-black text-orange-400">
                        ${(detailsAddon.price / 100).toFixed(2)}
                      </p>
                    </div>
                    {detailsAddon.hasInventory && liveStock !== null && !isOutOfStock && (
                      <div className="text-right">
                        <p className="text-sm text-orange-200">Available</p>
                        <p className={`text-lg font-bold ${isLowStock ? 'text-red-400' : 'text-white'}`}>
                          {liveStock} in stock
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Description */}
                  {detailsAddon.description && (
                    <div>
                      <p className="text-sm font-medium text-white mb-1">Description</p>
                      <p className="text-sm text-orange-200 whitespace-pre-wrap">{detailsAddon.description}</p>
                    </div>
                  )}

                  {/* Max per order info */}
                  {detailsAddon.maxPerOrder && (
                    <p className="text-xs text-orange-200/60">
                      Maximum {detailsAddon.maxPerOrder} per order
                    </p>
                  )}

                  {/* Quantity controls */}
                  <div className="pt-2">
                    {isOutOfStock ? (
                      <div className="space-y-2">
                        <div className="flex items-center justify-center gap-2 py-3 px-4 rounded-lg bg-gray-600/50 text-gray-300">
                          <span className="font-medium">Out of Stock</span>
                        </div>
                        {addonWaitlistEntries[detailsAddon.id] ? (
                          <div className="flex items-center justify-center gap-2">
                            <span className="text-sm text-orange-200">
                              <ListOrdered className="h-4 w-4 inline mr-1" />
                              You're on the waitlist
                            </span>
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-xs border-gray-500 text-gray-300 hover:bg-gray-500/20"
                              onClick={() => handleLeaveAddonWaitlist(detailsAddon.id)}
                              disabled={addonWaitlistLoading}
                            >
                              Leave
                            </Button>
                          </div>
                        ) : (
                          <Button
                            size="sm"
                            className="w-full bg-orange-500 hover:bg-orange-600 text-white"
                            onClick={() => handleJoinAddonWaitlist(detailsAddon.id)}
                            disabled={addonWaitlistLoading}
                          >
                            <ListOrdered className="h-4 w-4 mr-1" />
                            Join Waitlist
                          </Button>
                        )}
                      </div>
                    ) : (
                      <div className="flex items-center justify-between p-3 bg-turquoise-700/50 rounded-lg">
                        <div className="flex items-center gap-3" role="group" aria-label={`Quantity selector for ${detailsAddon.name}`}>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleQuantityChange(detailsAddon.id, -1, detailsAddon)}
                            disabled={quantity === 0}
                            className="h-10 w-10 p-0 border-orange-400 text-orange-300 hover:bg-orange-500/30"
                            aria-label={`Decrease quantity of ${detailsAddon.name}`}
                          >
                            <Minus className="h-5 w-5" aria-hidden="true" />
                          </Button>
                          <span
                            className="w-12 text-center font-bold text-xl text-white"
                            aria-label={`Current quantity: ${quantity}`}
                            role="status"
                          >
                            {quantity}
                          </span>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleQuantityChange(detailsAddon.id, 1, detailsAddon)}
                            disabled={
                              (detailsAddon.maxPerOrder !== null && quantity >= detailsAddon.maxPerOrder) ||
                              (detailsAddon.hasInventory && liveStock !== null && quantity >= liveStock)
                            }
                            className="h-10 w-10 p-0 border-orange-400 text-orange-300 hover:bg-orange-500/30"
                            aria-label={`Increase quantity of ${detailsAddon.name}`}
                          >
                            <Plus className="h-5 w-5" aria-hidden="true" />
                          </Button>
                        </div>
                        {quantity > 0 && (
                          <p className="text-lg font-bold text-orange-400">
                            ${((detailsAddon.price * quantity) / 100).toFixed(2)}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Existing Purchases Section */}
      {existingPurchases.length > 0 && (
        <Card className="backdrop-blur-md bg-turquoise-700/80 border-2 border-turquoise-500">
          <CardHeader>
            <CardTitle className="font-viking text-xl flex items-center gap-2 text-white">
              <PackageCheck className="h-5 w-5 text-orange-300" />
              Your Previous Purchases
            </CardTitle>
            <p className="text-sm text-orange-200">
              These items have already been purchased and will not be added to your cart again.
            </p>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {existingPurchases.map((purchase) => {
                const addon = addons.find((a) => a.id === purchase.addonId);
                if (!addon) return null;

                return (
                  <div
                    key={purchase.addonId}
                    className="flex items-center gap-4 p-3 bg-turquoise-600/50 rounded-lg border border-turquoise-500"
                  >
                    {addon.imageUrl && (
                      <img
                        src={addon.imageUrl}
                        alt={addon.name}
                        className="w-16 h-16 object-cover rounded-md"
                      />
                    )}
                    <div className="flex-1">
                      <p className="font-medium text-white">{addon.name}</p>
                      <p className="text-sm text-orange-200">
                        Quantity: {purchase.quantity} • ${((addon.price * purchase.quantity) / 100).toFixed(2)}
                      </p>
                    </div>
                    <Badge variant="outline" className="bg-turquoise-500 text-white border-turquoise-400">
                      Purchased
                    </Badge>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      <Card className="backdrop-blur-md bg-turquoise-800/90 border-2 border-orange-500">
        <CardHeader>
          <CardTitle className="font-viking text-2xl flex items-center gap-2 text-white">
            <ShoppingBag className="h-6 w-6 text-orange-400" />
            Add-ons & Merchandise
          </CardTitle>
          <p className="text-sm text-orange-200">
            Optional items to enhance your event experience. All proceeds support the event!
          </p>
        </CardHeader>
        <CardContent>
          {addons.length === 0 ? (
            <div className="text-center py-8">
              <ShoppingBag className="h-12 w-12 text-orange-300 mx-auto mb-3" />
              <p className="text-orange-200 font-medium">
                No merchandise available at this time.
              </p>
              <p className="text-sm text-orange-200/80 mt-2">
                You can continue with your registration without selecting any merchandise.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {addons.map((addon) => {
                const quantity = getQuantity(addon.id);
                const liveStock = getLiveStock(addon);
                const isOutOfStock =
                  addon.hasInventory && liveStock !== null && liveStock === 0;
                const isMaxed =
                  addon.maxPerOrder !== null && quantity >= addon.maxPerOrder;
                const isLowStock =
                  addon.hasInventory &&
                  liveStock !== null &&
                  liveStock <= 5 &&
                  liveStock > 0;
                const hasQuantity = quantity > 0;

                return (
                  <div
                    key={addon.id}
                    data-testid="addon-card"
                    className={`
                      group relative rounded-xl overflow-hidden transition-all duration-300
                      ${hasQuantity
                        ? 'shadow-[0_0_25px_rgba(249,115,22,0.4)] ring-2 ring-orange-500'
                        : 'shadow-lg hover:shadow-[0_0_15px_rgba(20,184,166,0.2)]'}
                      ${isOutOfStock ? 'opacity-60' : ''}
                    `}
                  >
                    {/* Top section with gradient */}
                    <div className={`
                      flex gap-4 p-4 pb-3 bg-gradient-to-br transition-all duration-300
                      ${hasQuantity
                        ? 'from-orange-500 to-orange-600'
                        : isOutOfStock
                          ? 'from-gray-600 to-gray-700'
                          : 'from-turquoise-600 to-turquoise-700 group-hover:from-turquoise-500 group-hover:to-turquoise-600'}
                    `}>
                      {/* Image */}
                      {addon.imageUrl && (
                        <div className="flex-shrink-0">
                          <img
                            src={addon.imageUrl}
                            alt={addon.name}
                            className="w-24 h-24 object-cover rounded-lg shadow-md"
                          />
                        </div>
                      )}

                      {/* Details */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <h3 className={`font-bold text-lg ${hasQuantity ? 'text-turquoise-100' : isOutOfStock ? 'text-gray-200' : 'text-white'}`}>{addon.name}</h3>
                            <div className="flex flex-wrap items-center gap-2 mt-1">
                              <Badge className={`text-xs capitalize ${hasQuantity ? 'bg-turquoise-500 text-white' : 'bg-orange-500 text-white'}`}>
                                {addon.category}
                              </Badge>
                              {addon.featured && (
                                <Badge className={`text-xs ${hasQuantity ? 'bg-turquoise-600 text-white' : 'bg-orange-600 text-white'}`}>Featured</Badge>
                              )}
                              {/* Inventory indicator - show for all items with inventory tracking */}
                              {addon.hasInventory && liveStock !== null && !isOutOfStock && !isLowStock && (
                                <Badge className="text-xs bg-turquoise-600/80 text-white">
                                  {liveStock} in stock
                                </Badge>
                              )}
                              {isLowStock && (
                                <Badge variant="destructive" className="text-xs animate-pulse">
                                  Only {liveStock} left!
                                </Badge>
                              )}
                              {isOutOfStock && (
                                <Badge variant="destructive" className="text-xs">
                                  Out of Stock
                                </Badge>
                              )}
                            </div>
                          </div>
                          <p className={`font-black text-2xl whitespace-nowrap ${hasQuantity ? 'text-turquoise-100' : isOutOfStock ? 'text-gray-300' : 'text-orange-400'}`}>
                            ${(addon.price / 100).toFixed(0)}
                          </p>
                        </div>

                        {addon.description && (
                          <>
                            <p className={`text-sm mt-2 line-clamp-2 ${hasQuantity ? 'text-turquoise-200' : isOutOfStock ? 'text-gray-300' : 'text-orange-200'}`}>
                              {addon.description}
                            </p>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setDetailsAddon(addon);
                              }}
                              className={`
                                mt-2 flex items-center gap-1.5 py-1 px-2 rounded text-xs font-medium
                                transition-colors duration-200
                                ${hasQuantity
                                  ? 'bg-turquoise-500/30 text-turquoise-100 hover:bg-turquoise-500/50'
                                  : isOutOfStock
                                    ? 'bg-gray-500/30 text-gray-200 hover:bg-gray-500/50'
                                    : 'bg-orange-500/30 text-orange-200 hover:bg-orange-500/50'}
                              `}
                            >
                              <Info className="h-3 w-3" aria-hidden="true" />
                              View Details
                            </button>
                          </>
                        )}
                      </div>
                    </div>

                    {/* Perforated divider */}
                    <div className={`relative h-3 ${hasQuantity ? 'bg-orange-700/50' : isOutOfStock ? 'bg-gray-700/50' : 'bg-turquoise-800/50'}`}>
                      <div className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1/2 w-6 h-6 rounded-full bg-turquoise-800"></div>
                      <div className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2 w-6 h-6 rounded-full bg-turquoise-800"></div>
                      <div className={`absolute inset-x-6 top-1/2 border-t-2 border-dashed ${hasQuantity ? 'border-orange-400/50' : 'border-turquoise-600/50'}`}></div>
                    </div>

                    {/* Bottom section - Quantity controls */}
                    <div className={`
                      p-4 pt-3
                      ${hasQuantity ? 'bg-orange-600/90' : isOutOfStock ? 'bg-gray-700/80' : 'bg-turquoise-700/80'}
                    `}>
                      {!isOutOfStock ? (
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3" role="group" aria-label={`Quantity selector for ${addon.name}`}>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleQuantityChange(addon.id, -1, addon)}
                              disabled={quantity === 0}
                              className={`
                                h-10 w-10 p-0 transition-all duration-300
                                ${quantity > 0
                                  ? 'border-turquoise-400 text-turquoise-200 hover:bg-turquoise-500/30'
                                  : 'border-orange-400 text-orange-300 hover:border-orange-300 hover:text-orange-200'}
                              `}
                              aria-label={`Decrease quantity of ${addon.name}`}
                            >
                              <Minus className="h-5 w-5" aria-hidden="true" />
                            </Button>
                            <span 
                              className={`
                                w-12 text-center font-bold text-xl transition-colors duration-300
                                ${quantity > 0 ? 'text-turquoise-100' : 'text-orange-200'}
                              `}
                              aria-label={`Current quantity: ${quantity}`}
                              role="status"
                            >
                              {quantity}
                            </span>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleQuantityChange(addon.id, 1, addon)}
                              disabled={isMaxed || (addon.hasInventory && liveStock !== null && quantity >= liveStock)}
                              className={`h-10 w-10 p-0 ${hasQuantity ? 'border-turquoise-400 text-turquoise-200 hover:bg-turquoise-500/30' : 'border-orange-400 text-orange-300 hover:bg-orange-500/30'}`}
                              aria-label={`Increase quantity of ${addon.name}`}
                            >
                              <Plus className="h-5 w-5" aria-hidden="true" />
                            </Button>
                          </div>
                          <div className="text-right">
                            {/* Show available stock when user has selected items */}
                            {addon.hasInventory && liveStock !== null && quantity > 0 && (
                              <span className={`text-xs block ${liveStock - quantity <= 3 ? 'text-amber-400' : 'text-turquoise-200/80'}`}>
                                {liveStock - quantity} remaining after selection
                              </span>
                            )}
                            {addon.maxPerOrder && (
                              <span className={`text-xs block ${hasQuantity ? 'text-turquoise-200/60' : 'text-orange-200/60'}`}>
                                Max {addon.maxPerOrder} per order
                              </span>
                            )}
                            {quantity > 0 && (
                              <p className="text-lg font-bold text-turquoise-100">
                                ${((addon.price * quantity) / 100).toFixed(2)}
                              </p>
                            )}
                          </div>
                        </div>
                      ) : (
                        <div className="text-center space-y-2">
                          <p className="text-sm text-red-400">
                            This item is currently out of stock
                          </p>
                          {addonWaitlistEntries[addon.id] ? (
                            <div className="flex items-center justify-center gap-2">
                              <span className="text-xs text-orange-200">
                                <ListOrdered className="h-3 w-3 inline mr-1" />
                                You're on the waitlist
                              </span>
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-xs h-7 border-gray-500 text-gray-300 hover:bg-gray-500/20"
                                onClick={() => handleLeaveAddonWaitlist(addon.id)}
                                disabled={addonWaitlistLoading}
                              >
                                Leave
                              </Button>
                            </div>
                          ) : (
                            <Button
                              size="sm"
                              className="text-xs h-7 bg-orange-500 hover:bg-orange-600 text-white"
                              onClick={() => handleJoinAddonWaitlist(addon.id)}
                              disabled={addonWaitlistLoading}
                            >
                              <ListOrdered className="h-3 w-3 mr-1" />
                              Join Waitlist
                            </Button>
                          )}
                          {waitlistError && (
                            <p className="text-xs text-red-400 mt-1">{waitlistError}</p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Cart Summary */}
      <Card className="backdrop-blur-md bg-turquoise-800/90 border-2 border-orange-500">
        <CardContent className="py-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-orange-200">
                {totalItems} item{totalItems !== 1 ? 's' : ''} selected
              </p>
              <p className="text-2xl font-bold text-white">${(totalCost / 100).toFixed(2)}</p>
            </div>
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={onBack}
                className="min-w-[120px] border-orange-500 text-orange-200 hover:bg-orange-500/20"
                aria-label="Go back to tournament selection"
              >
                <ChevronLeft className="h-4 w-4 mr-1" aria-hidden="true" />
                Back
              </Button>
              <Button 
                onClick={handleContinue} 
                variant="secondary" 
                className="min-w-[120px]"
                aria-label="Continue to checkout"
              >
                Continue to Checkout
              </Button>
            </div>
          </div>

          {selections.length > 0 && (
            <div className="mt-4 pt-4 border-t border-turquoise-600 space-y-2">
              <p className="text-sm font-medium text-white">Selected items:</p>
              <ul className="space-y-1">
                {selections.map((selection) => {
                  const addon = addons.find((a) => a.id === selection.addonId);
                  if (!addon) return null;
                  return (
                    <li
                      key={selection.addonId}
                      className="text-sm text-orange-200 flex justify-between"
                    >
                      <span>
                        {addon.name} × {selection.quantity}
                      </span>
                      <span>${((addon.price * selection.quantity) / 100).toFixed(2)}</span>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
